import { action, computed } from "@ember/object"
import { inject as service } from "@ember/service"
import Component from "@glimmer/component"
import { tracked } from "@glimmer/tracking"
import { A, pipe } from "@mobily/ts-belt"
import type { Group } from "@shared/models/group"
import type { PlanbookSummary } from "@shared/models/planbook"
import type SchoolYear from "@shared/models/school-year"
import { CURRENT_SCHOOL_YEAR_ID } from "@shared/models/school-year"
import type { User } from "@shared/models/user"
import { handleError } from "cc-frontend/error-handling"
import findMany from "cc-frontend/helpers/find-many"
import LiveQueryResource from "cc-frontend/helpers/live-query"
import type { PromiseObject } from "cc-frontend/lib/promise-object"
import type Dialog from "cc-frontend/services/dialog"
import type EmberStore from "cc-frontend/services/ember-store"
import type ModalManager from "cc-frontend/services/modal-manager"
import type Query from "cc-frontend/services/query"
import type Rpc from "cc-frontend/services/rpc"
import type Session from "cc-frontend/services/session"
import {
  isCachedMembershipActiveInclAncestors,
  isCachedMembershipActiveSchool,
} from "cc-frontend/utils/group"
import parseEmails from "cc-frontend/utils/parse-emails"
import { toDown, toUp } from "ember-animated/transitions/move-over"
import { use } from "ember-could-get-used-to-this"
import _ from "lodash"
import * as R from "remeda"

interface Sig {
  Args: {
    user: User
  }
}

export default class UserHome extends Component<Sig> {
  @service("ember-store") declare store: EmberStore
  @service declare rpc: Rpc
  @service declare query: Query
  @service declare dialog: Dialog
  @service declare session: Session
  @service declare modalManager: ModalManager

  // Constants

  emptySelectedOption = []

  rules({ newItems }) {
    if (newItems[0]) {
      return toDown
    } else {
      return toUp
    }
  }

  // Variables

  @tracked selectedSchoolYearId = CURRENT_SCHOOL_YEAR_ID
  @tracked referralEmails: string | null = null
  @tracked isProcessingReferrals = false

  // Getters

  @use planbooksQuery = new LiveQueryResource(() => {
    let queryId = this.args.user.id
    return ["planbooks_for_user", queryId]
  })

  get groupedPlanbooks() {
    let grouped = _.groupBy(this.planbooksQuery.results, (planbook) => {
      return (planbook as unknown as PlanbookSummary).attributes.schoolYearId
    })
    // We use this to make sure the current school year is always present
    grouped[CURRENT_SCHOOL_YEAR_ID] = grouped[CURRENT_SCHOOL_YEAR_ID] || []

    return R.pipe(
      grouped,
      R.toPairs,
      R.map(([schoolYearId, planbooks]) => {
        return {
          schoolYearId: schoolYearId,
          planbooks: planbooks,
          isExpanded: schoolYearId === this.selectedSchoolYearId,
        }
      }),
      R.reject((group) => group.schoolYearId === "null"),
      R.reject((group) => group.schoolYearId === "TEMPLATE"),
      R.sortBy((group) => group.schoolYearId),
      R.reverse
    )
  }

  get isAdmin(): boolean {
    return ["admin", "district-admin"].includes(this.args.user.attributes.educationRole)
  }

  @computed("args.user.attributes.cachedMemberships.[]")
  get schoolGroupIds() {
    return pipe(
      this.args.user.attributes.cachedMemberships,
      A.filter(isCachedMembershipActiveSchool),
      A.map((membership) => membership.groupId)
    )
  }

  @computed("args.user.attributes.cachedMemberships.[]")
  get schoolNetworkGroupIds(): string[] {
    return pipe(
      this.args.user.attributes.cachedMemberships,
      A.filter(isCachedMembershipActiveInclAncestors),
      A.filter((membership) => membership.usedAs === "school-network"),
      A.map((membership) => membership.groupId),
      A.uniq
    )
  }

  @computed("args.user.attributes.cachedMemberships.[]")
  get requestedGroupIds(): string[] {
    return pipe(
      this.args.user.attributes.cachedMemberships,
      A.filter(
        (membership) =>
          membership.state === "open" &&
          ["school", "school-network"].includes(membership.usedAs) &&
          !membership.isAncestor &&
          membership.membershipType === "group-membership-requested"
      ),
      A.map((membership) => membership.groupId),
      A.uniq
    )
  }

  @computed("args.user.attributes.cachedMemberships.[]")
  get potentialGroupIds(): string[] {
    return pipe(
      this.args.user.attributes.cachedMemberships,
      A.filter(
        (membership) =>
          membership.state === "open" &&
          ["school", "school-network"].includes(membership.usedAs) &&
          !membership.isAncestor &&
          membership.membershipType === "group-membership-potential"
      ),
      A.map((membership) => membership.groupId),
      A.uniq
    )
  }

  @computed("args.user.attributes.cachedMemberships.[]")
  get lockedGroupIds(): string[] {
    return pipe(
      this.args.user.attributes.cachedMemberships,
      A.filter(
        (membership) =>
          membership.state === "locked" &&
          ["school", "school-network"].includes(membership.usedAs) &&
          !membership.isAncestor &&
          [
            "group-membership-accepted",
            "group-membership-requested",
            "group-membership-potential",
          ].includes(membership.membershipType)
      ),
      A.map((membership) => membership.groupId),
      A.uniq
    )
  }

  @findMany("schoolGroupIds", "group") schools!: PromiseObject<Array<Group>>

  @findMany("schoolNetworkGroupIds", "group") schoolNetworks!: PromiseObject<Array<Group>>

  @findMany("requestedGroupIds", "group") requestedGroups!: PromiseObject<Array<Group>>

  @findMany("potentialGroupIds", "group") potentialGroups!: PromiseObject<Array<Group>>

  @findMany("lockedGroupIds", "group") lockedGroups!: PromiseObject<Array<Group>>

  @computed("args.user.attributes.cachedMemberships.length")
  get showLockedGroups(): boolean {
    return (
      this.schoolGroupIds.length === 0 &&
      this.schoolNetworkGroupIds.length === 0 &&
      this.requestedGroupIds.length === 0 &&
      this.potentialGroupIds.length === 0
    )
  }

  // Actions

  @action
  selectSchoolYear(schoolYear: SchoolYear) {
    this.selectedSchoolYearId = schoolYear.id
  }

  @action
  goToAccount() {
    this.modalManager.openModal("user/account", {
      userId: this.args.user.id,
    })
  }

  @action
  createOrCopyPlanbook(opt) {
    if (opt.id === "CREATE") {
      this.modalManager.openModal("user/create-planbook", {
        userId: this.args.user.id,
      })
    } else if (opt.id === "COPY") {
      this.modalManager.openModal("planner/planbook/copy-planbook/modal", {
        user: this.args.user,
      })
    }
  }

  @action
  submitReferrals() {
    let emails = this.referralEmails!
    let emailArray = parseEmails(emails)

    this.isProcessingReferrals = true

    this.rpc.execute("INVITE_TO_CC", { emails: emailArray }).then(() => {
      this.referralEmails = null
      this.isProcessingReferrals = false
      this.dialog
        .blank("dialogs/alert-people-invited", {
          className: "dialog--create-annotation",
        })
        .catch((error) => {
          if (error instanceof Error) handleError(error)
        })
    })
  }
}

declare module "@glint/environment-ember-loose/registry" {
  export default interface Registry {
    "Users::Home": typeof UserHome
  }
}
